import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { Edit } from "react-feather";
import DataTable from "../../../components/common/DataTable";
import { confirmDelete } from "../../../components/common/ConfirmDialog";
import { useBranchFilter } from "../../branchFilter/useBranchFilter";
import { useCanCreateAreas, useCanEditAreas } from "./useAreaPermissions";
import {
  useGetAreasQuery,
  useCreateAreaMutation,
  useUpdateAreaMutation,
  useDeleteAreaMutation,
} from "./areasApi";

// Branch-wise master (revised 2026-08-13 - see areasApi.js) - mirrors
// ProductGroupsPage.jsx's pattern: a Branch field on the record itself, the
// list scoped by the header branch picker (server-side ?branchId= filter,
// confirmed live), no all-branch requirement to write (unlike the genuinely
// global masters).
const AreasPage = () => {
  const canCreate = useCanCreateAreas();
  const canEdit = useCanEditAreas();
  const { selectedBranchId, availableBranches } = useBranchFilter();
  const { data: records = [], isLoading } = useGetAreasQuery({
    branchId: selectedBranchId ?? undefined,
  });
  const [createArea, { isLoading: isCreating }] = useCreateAreaMutation();
  const [updateArea, { isLoading: isUpdating }] = useUpdateAreaMutation();
  const [deleteArea] = useDeleteAreaMutation();

  const [mode, setMode] = useState("new"); // "new" | "view" | "edit"
  const [selectedKey, setSelectedKey] = useState(null); // `${branchId}-${areaId}`
  const [branchId, setBranchId] = useState("");
  const [name, setName] = useState("");
  const [search, setSearch] = useState("");

  const selectedRecord =
    records.find((r) => `${r.branchId}-${r.areaId}` === selectedKey) || null;

  // 80 real legacy areas spread across branches - a plain client-side name
  // filter is enough at this scale, no server-side search param needed.
  const filteredRecords = search.trim()
    ? records.filter((r) => r.areaName.toLowerCase().includes(search.trim().toLowerCase()))
    : records;

  useEffect(() => {
    if (mode === "new") {
      setName("");
      setBranchId(
        selectedBranchId ? String(selectedBranchId) : availableBranches[0]?.branchId ? String(availableBranches[0].branchId) : ""
      );
    } else if (selectedRecord) {
      setBranchId(String(selectedRecord.branchId));
      setName(selectedRecord.areaName);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, selectedRecord]);

  const isReadOnly = mode === "view";
  const canWrite = mode === "new" ? canCreate : canEdit;

  const select = (key) => {
    setSelectedKey(key);
    setMode("view");
  };
  const startEdit = () => setMode("edit");
  const startNew = () => {
    setSelectedKey(null);
    setMode("new");
  };

  const handleSave = async () => {
    if (!branchId) {
      toast.error("Branch is required");
      return;
    }
    if (!name.trim()) {
      toast.error("Name is required");
      return;
    }
    try {
      if (mode === "edit" && selectedRecord) {
        await updateArea({
          branchId: selectedRecord.branchId,
          areaId: selectedRecord.areaId,
          areaName: name.trim(),
        }).unwrap();
      } else {
        await createArea({ branchId: Number(branchId), areaName: name.trim() }).unwrap();
      }
      toast.success("Area saved");
      startNew();
    } catch (error) {
      toast.error(error?.message || "Could not save area");
    }
  };

  const handleDelete = async () => {
    if (!selectedRecord) return;
    const confirmed = await confirmDelete({ title: `Delete ${selectedRecord.areaName}?` });
    if (!confirmed) return;
    try {
      await deleteArea({ branchId: selectedRecord.branchId, areaId: selectedRecord.areaId }).unwrap();
      toast.success("Area deleted");
      startNew();
    } catch (error) {
      toast.error(error?.message || "Could not delete area");
    }
  };

  const columns = [
    { title: "Area", dataIndex: "areaName" },
    { title: "Branch", dataIndex: "branchCode" },
    {
      title: "",
      dataIndex: "actions",
      width: 60,
      render: (_, record) => (
        <button
          type="button"
          className="btn btn-link p-1"
          onClick={() => select(`${record.branchId}-${record.areaId}`)}
        >
          <Edit size={14} />
        </button>
      ),
    },
  ];

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="page-header">
          <div className="add-item d-flex">
            <div className="page-title">
              <h4>Areas</h4>
              <h6>Area master data, per branch</h6>
            </div>
          </div>
        </div>

        {!canCreate && !canEdit && (
          <div className="alert alert-info">
            You have read-only access. Managing areas requires Area.Create/.Edit.
          </div>
        )}

        <div className="card mb-4">
          <div className="card-body">
            <h5 className="mb-3">Details</h5>
            <div className="row">
              <div className="col-md-3 mb-3">
                <label className="form-label">Branch</label>
                <select
                  className="form-control"
                  value={branchId}
                  onChange={(e) => setBranchId(e.target.value)}
                  disabled={isReadOnly || mode === "edit" || !canWrite}
                >
                  <option value="">--Select--</option>
                  {availableBranches.map((b) => (
                    <option key={b.branchId} value={b.branchId}>{b.branchCode}</option>
                  ))}
                </select>
              </div>
              <div className="col-md-7 mb-3">
                <label className="form-label">Name</label>
                <input
                  type="text"
                  className="form-control"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  readOnly={isReadOnly || !canWrite}
                  disabled={isReadOnly || !canWrite}
                />
              </div>
            </div>
            {(canCreate || canEdit) && (
              <div className="d-flex gap-2">
                {mode !== "view" && canWrite && (
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleSave}
                    disabled={isCreating || isUpdating}
                  >
                    Save
                  </button>
                )}
                {mode === "view" && canEdit && (
                  <button type="button" className="btn btn-outline-primary" onClick={startEdit}>
                    Edit
                  </button>
                )}
                <button type="button" className="btn btn-secondary" onClick={startNew}>
                  Clear
                </button>
                {mode === "view" && canEdit && (
                  <button type="button" className="btn btn-outline-danger" onClick={handleDelete}>
                    Delete
                  </button>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="card table-list-card">
          <div className="card-body">
            <div className="table-top">
              <div className="search-set">
                <div className="search-input">
                  <input
                    type="text"
                    placeholder="Search area name"
                    className="form-control form-control-sm formsearch"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                </div>
              </div>
            </div>
            <div className="table-responsive">
              <DataTable
                columns={columns}
                dataSource={filteredRecords}
                rowKey={(r) => `${r.branchId}-${r.areaId}`}
                loading={isLoading}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AreasPage;
